/**
 * ScanProgressBar - Shows portfolio scan progress during analysis.
 *
 * Displays: "Analyzing X of Y positions... (Z cached)"
 * with an animated progress bar underneath.
 */

import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ScanProgress } from '@/types/portfolio';

interface ScanProgressBarProps {
  progress: ScanProgress;
  className?: string;
}

export function ScanProgressBar({ progress, className }: ScanProgressBarProps) {
  const { total, completed, cached, failed } = progress;
  const done = completed + failed;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className={cn(
      'bg-surface border border-border rounded-lg p-4',
      className,
    )}>
      <div className="flex items-center gap-3 mb-2">
        <Loader2 className="w-4 h-4 text-primary animate-spin" />
        <span className="text-sm text-text-secondary">
          Analyzing {done} of {total} positions
          {cached > 0 && (
            <span className="text-text-muted"> ({cached} cached)</span>
          )}
          {failed > 0 && (
            <span className="text-red-400"> ({failed} failed)</span>
          )}
        </span>
        <span className="ml-auto text-xs text-text-muted tabular-nums">{pct}%</span>
      </div>
      <div className="h-1.5 bg-surface-light rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-300 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
